import { LoadingOutlined } from '@ant-design/icons';
import { message, Pagination, Spin } from 'antd';
import { downloadFileFromBlob } from 'helpers/util.helpers';
import { defaultApiErrorSchema, QueryData } from 'lib/api/utils';
import { useGetRecentEsimOrders } from 'lib/hooks/dashboard/dashboard.queries';
import { useGetOrdersReport } from 'lib/hooks/order/order.mutations';
import moment from 'moment';
import React from 'react';
import NumberFormat from 'react-number-format';
import { OrderStatus } from 'types/shared';
import { orderStatusLabelMap, ORDERS_COUNT_PER_PAGE } from '../../config';
import ImpersonateConfirmationModal from '../ImpersonateConfirmationModal/ImpersonateConfirmationModal';

export type RecentOrders = QueryData<
  typeof useGetRecentEsimOrders
>['ordersArray'];
export type Order = RecentOrders[0];

function RecentEsimOrdersList() {
  const [page, setPage] = React.useState(1);
  const [selectedOrder, setSelectedOrder] = React.useState<Order | null>(null);
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] =
    React.useState(false);

  const { data: recentEsimOrdersData, isLoading: isEsimOrdersLoading } =
    useGetRecentEsimOrders({
      limit: ORDERS_COUNT_PER_PAGE,
      page: page,
    });

  const getOrderReportMutation = useGetOrdersReport();

  const exportOrdersHandler = async () => {
    try {
      const csvText = await getOrderReportMutation.mutateAsync({
        orderType: 'eSIM',
      });

      const blob = new Blob([csvText], { type: 'text/csv' });

      const fileName = 'orders-report-' + moment().format('YYYY-MM-DD');

      downloadFileFromBlob(blob, `${fileName}.csv`);
    } catch (e) {
      const errorMessage =
        defaultApiErrorSchema.safeParse(e)?.data?.data?.message ??
        'Something went wrong. Please try again.';

      message.open({
        content: errorMessage,
        type: 'error',
      });
    }
  };

  const openImpersonateModal = (order: Order) => {
    setSelectedOrder(order);
    setIsImpersonateModalOpen(true);
  };

  const closeImpersonateModal = () => {
    setSelectedOrder(null);
    setIsImpersonateModalOpen(false);
  };

  const orders = recentEsimOrdersData?.ordersArray ?? [];
  const noRecordsFound = !isEsimOrdersLoading && !orders.length;

  const renderContent = () => {
    if (noRecordsFound) return <EmptyState />;

    return (
      <RecentEsimOrdersTable
        orders={orders}
        openImpersonateModal={openImpersonateModal}
      />
    );
  };

  return (
    // @ts-ignore
    <Spin
      spinning={isEsimOrdersLoading}
      size="large"
      indicator={
        // @ts-ignore
        <LoadingOutlined
          style={{ fontSize: 30, color: '#000' }}
          spin
          data-testid="loading-spinner"
        />
      }
    >
      <div className="card mb-3">
        <div className="card-header d-flex align-items-center">
          <h5 className="card-title">
            Most Recent eSIM Orders from SPR Partners
          </h5>

          <button
            className="btn btn-purple w-200 ml-auto"
            onClick={exportOrdersHandler}
            disabled={getOrderReportMutation.isLoading}
          >
            Export
          </button>
        </div>

        {renderContent()}
      </div>

      <div className="text-left">
        <Pagination
          className="mb-5"
          hideOnSinglePage
          onChange={setPage}
          current={page}
          pageSize={ORDERS_COUNT_PER_PAGE}
          defaultCurrent={page}
          total={recentEsimOrdersData?.totalOrders}
          showSizeChanger={false}
        />
      </div>

      <ImpersonateConfirmationModal
        isOpen={isImpersonateModalOpen}
        onClose={closeImpersonateModal}
        selectedOrder={selectedOrder}
      />
    </Spin>
  );
}

export default RecentEsimOrdersList;

function EmptyState() {
  return (
    <div className="blank__table__card" style={{ boxShadow: 'none' }}>
      <div className="blank__table__card__inner">No records found</div>
    </div>
  );
}

type RecentEsimOrdersTableProps = {
  orders: RecentOrders;
  openImpersonateModal: (order: Order) => void;
};

function RecentEsimOrdersTable({
  orders,
  openImpersonateModal,
}: RecentEsimOrdersTableProps) {
  return (
    <div className="table-responsive">
      <table className="table custom-table-secondary table-nowidth">
        <thead>
          <tr>
            {[
              'Order Date',
              'Order Status',
              'Company Name',
              'Order Number',
              'Ordered By',
              'Ordered Sims',
            ].map((t) => (
              <th key={t}>
                <span className="table-column-title">{t}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {orders.map((o) => {
            const {
              orderId,
              orderDate,
              orderedBy,
              companyName,
              orderNumber,
              orderedSims,
              status,
            } = o;

            const orderDateContent = orderDate
              ? moment(orderDate).format('MMM DD, YYYY h:mm a z')
              : '-';

            const orderStatus = status as OrderStatus;

            return (
              <tr key={orderId}>
                <td>{orderDateContent}</td>
                <td>{orderStatusLabelMap[orderStatus] ?? '-'}</td>
                <td>{companyName}</td>
                <td>{orderNumber}</td>
                <td>
                  <span
                    className="text-underline btn-link-orange"
                    onClick={() => openImpersonateModal(o)}
                  >
                    {orderedBy}
                  </span>
                </td>

                <td>
                  <NumberFormat
                    thousandSeparator={true}
                    displayType={'text'}
                    value={orderedSims}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
